class ImageGalleryItem
{
    public readonly thumbnailElement: HTMLButtonElement;
    public readonly imageElement: HTMLElement;

    private selected: boolean;

    constructor(thumbnailElement: HTMLButtonElement, imageElement: HTMLElement)
    {
        this.thumbnailElement = thumbnailElement;
        this.imageElement = imageElement;
        this.selected = false;
    }

    public get Selected(): boolean
    {
        return this.selected;
    }

    public set Selected(selected: boolean)
    {
        if (this.selected !== selected)
        {
            if (selected)
            {
                this.thumbnailElement.dataset.state = "active";
                this.imageElement.dataset.state = "active";
            }
            else
            {
                delete this.thumbnailElement.dataset.state;
                delete this.imageElement.dataset.state;
            }
            this.selected = selected;
        }
    }
}

interface ImageGalleryEventListeners
{
    onClick: EventListener;
    onSelectItemPhase1: FrameRequestCallback;
    onSelectItemPhase2: FrameRequestCallback;
    onSelectItemPhase3: FrameRequestCallback;
}

export class ImageGalleryController
{
    private readonly rootElement: HTMLElement;
    private readonly items: ImageGalleryItem[];
    private readonly eventListeners: ImageGalleryEventListeners;
    private readonly imagePaneElement: HTMLElement;

    private activeItem?: ImageGalleryItem;
    private preActiveItem?: ImageGalleryItem;
    private eventSubscription: boolean;
    private animationFrameHandle?: number;

    constructor(rootElement: HTMLElement)
    {
        this.rootElement = rootElement;

        const imagePaneElement = rootElement.querySelector<HTMLElement>(":scope [data-image-gallery-pane]");
        if (imagePaneElement === null)
        {
            throw new Error("Image gallery pane not found.");
        }

        this.imagePaneElement = imagePaneElement;

        const thumbnails = Array.from(rootElement.querySelectorAll<HTMLButtonElement>(":scope button[data-image-gallery-id]"));
        const images = Array.from(rootElement.querySelectorAll<HTMLElement>(":scope figure[data-image-gallery-id]"));

        this.items = [];

        for (const thumbnail of thumbnails)
        {
            const galleryId = thumbnail.dataset.imageGalleryId;

            if (galleryId !== undefined && galleryId !== "")
            {
                const image = images.find(image => image.dataset.imageGalleryId === galleryId);
                if (image !== undefined)
                {
                    this.items.push(new ImageGalleryItem(thumbnail, image));
                }
            }
        }

        this.eventListeners = {
            onClick: this.handleClick.bind(this) as EventListener,
            onSelectItemPhase1: this.selectItemPhase1.bind(this) as FrameRequestCallback,
            onSelectItemPhase2: this.selectItemPhase2.bind(this) as FrameRequestCallback,
            onSelectItemPhase3: this.selectItemPhase3.bind(this) as FrameRequestCallback,
        };

        this.eventSubscription = false;

        this.subscribe();

        if (this.items.length > 0)
        {
            this.ActiveItem = this.items[0];
        }
    }

    public set ActiveItem(activeItem: ImageGalleryItem)
    {
        if (this.activeItem !== activeItem)
        {
            if (this.animationFrameHandle !== undefined)
            {
                cancelAnimationFrame(this.animationFrameHandle);
                this.animationFrameHandle = undefined;
            }

            this.preActiveItem = activeItem;
            this.animationFrameHandle = requestAnimationFrame(this.eventListeners.onSelectItemPhase1);
        }
    }

    public dispose(): void
    {
        this.unsubscribe();
    }

    private handleClick(event: MouseEvent): void
    {
        const target = event.target as HTMLElement;

        const activeItem = this.items.find(item => item.thumbnailElement.contains(target));

        if (activeItem !== undefined)
        {
            event.stopImmediatePropagation();
            this.ActiveItem = activeItem;
        }
    }

    private selectItemPhase1(): void
    {
        //this.imagePaneElement.dataset.state = "invisible";
        //this.imagePaneElement.style.opacity = "0";
        this.imagePaneElement.style.visibility = "hidden";

        const prevActiveItem = this.activeItem;

        if (prevActiveItem !== undefined)
        {
            this.activeItem = undefined;
            prevActiveItem.Selected = false;
        }

        const activeItem = this.preActiveItem;

        if (activeItem !== undefined)
        {
            this.preActiveItem = undefined;
            activeItem.Selected = true;
            this.activeItem = activeItem;
        }

        this.animationFrameHandle = requestAnimationFrame(this.eventListeners.onSelectItemPhase2);
    }

    private selectItemPhase2(): void
    {
        //this.imagePaneElement.dataset.state = "transparent";
        this.imagePaneElement.style.visibility = "visible";
        this.imagePaneElement.style.opacity = "0";

        this.animationFrameHandle = requestAnimationFrame(this.eventListeners.onSelectItemPhase3);
    }

    private selectItemPhase3(): void
    {
        this.animationFrameHandle = undefined;

        //delete this.imagePaneElement.dataset.state;
        this.imagePaneElement.style.opacity = "1";
    }

    private subscribe()
    {
        if (!this.eventSubscription)
        {
            this.rootElement.addEventListener("click", this.eventListeners.onClick, true);
            this.eventSubscription = true;
        }
    }

    private unsubscribe()
    {
        if (this.eventSubscription)
        {
            this.rootElement.removeEventListener("click", this.eventListeners.onClick, true);
            this.eventSubscription = false;
        }
    }
}